/**
 * useExecutionPolling — auto-polls execution status when the detail panel
 * shows a non-terminal execution (queued/running). When the execution
 * completes, it auto-updates the panel with full results (steps, screenshots).
 *
 * Polling interval: 3 seconds while queued/running, stops on terminal status.
 */

import { useEffect, useRef } from 'react'
import axios from 'axios'
import type { DetailPanelContent } from '../types'
import { isExecutionTerminal } from '../components/detail-renderers/ExecutionRenderer'

// NOTE: Uses raw axios with manual token (same pattern as tunnels.ts, recordings.ts).
// If auth changes (refresh tokens), consider migrating to a shared API client.
const API_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_ATHENA_API_URL || 'http://localhost:8002'
const POLL_INTERVAL_MS = 3000

/** Extract the execution UUID from a panel resourceKey like "execution:<uuid>" */
function extractExecutionId(resourceKey: string): string | null {
  const match = resourceKey.match(/^execution:([0-9a-f-]{36})$/i)
  return match ? match[1] : null
}

interface UseExecutionPollingConfig {
  /** Current panel content (null if panel is closed) */
  panelContent: DetailPanelContent | null
  /** Whether the panel is visible */
  panelVisible: boolean
  /** Callback to update the panel content with polled data */
  onExecutionUpdate: (resourceKey: string, data: Record<string, unknown>, title: string) => void
}

export function useExecutionPolling({
  panelContent,
  panelVisible,
  onExecutionUpdate,
}: UseExecutionPollingConfig) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Only poll when:
    // 1. Panel is visible
    // 2. Current resource is an execution
    // 3. Status is non-terminal
    if (!panelVisible || !panelContent) return
    if (panelContent.resourceType !== 'execution') return

    const currentData = panelContent.data as Record<string, unknown> | null
    const currentStatus = (currentData?.status as string) || ''
    if (isExecutionTerminal(currentStatus)) return

    const executionId = extractExecutionId(panelContent.resourceKey)
    if (!executionId) return

    const poll = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const response = await axios.get(`${API_URL}/api/v1/executions/${executionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        const execution = response.data
        const newStatus = execution.status || ''
        const steps = (execution.steps || []).map((s: Record<string, unknown>) => {
          // API returns screenshot_path (relative), convert to full URL
          let screenshotUrl = (s.screenshot_url as string) || null
          if (!screenshotUrl && s.screenshot_path) {
            const path = s.screenshot_path as string
            screenshotUrl = path.startsWith('http') ? path : `${API_URL}/screenshots/${path}`
          }
          return {
            step_number: s.step_number,
            action: s.action,
            success: s.success,
            step_type: s.step_type,
            error_category: s.error_category,
            reasoning: s.reasoning,
            error: s.error,
            duration_ms: s.duration_ms,
            screenshot_url: screenshotUrl,
          }
        })

        const updatedData: Record<string, unknown> = {
          ...(currentData || {}),
          status: newStatus,
          steps,
          step_count: execution.step_count || steps.length,
          screenshot_count: execution.screenshot_count,
          duration_seconds: execution.duration_seconds,
          result: execution.result || '',
          error: execution.error || '',
          target_url: execution.target_url || currentData?.target_url || '',
          test_case_name: currentData?.test_case_name || execution.test_case_name || '',
          history_url: currentData?.history_url || `/app/history?execution=${executionId}`,
        }

        const tcName = (updatedData.test_case_name as string) || 'Execution'
        const title = isExecutionTerminal(newStatus)
          ? `${tcName} — ${newStatus}`
          : `Running: ${tcName}`

        onExecutionUpdate(panelContent.resourceKey, updatedData, title)

        // Stop polling if terminal
        if (isExecutionTerminal(newStatus) && intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      } catch {
        // Silently ignore poll failures — next interval will retry
      }
    }

    // Start polling
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS)
    // Also poll immediately on mount
    void poll()

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [
    panelVisible,
    panelContent?.resourceKey,
    panelContent?.resourceType,
    // Re-evaluate when status changes (detected via data reference)
    (panelContent?.data as Record<string, unknown> | null)?.status,
  ])
}
